// CRITICAL: Initialize logger FIRST before any other imports
// This ensures all console methods are overridden before any code runs
import { initLogger } from './utils/logger'
initLogger()

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import App from './App.tsx'
import './styles/globals.css'
import '@cognixis-technology/common-react-components/styles'
import { initConsoleCapture } from './utils/consoleCapture'
import { initializeGlobalErrorHandlers } from './utils/globalErrorHandler'

// Initialize global error handlers before React renders
initializeGlobalErrorHandlers()

// Initialize console log capture for automatic error tracking
// Only capture when logging is enabled
if (import.meta.env.MODE === 'development') {
  initConsoleCapture()
}

// Global React error handler to capture React error #310 and other React errors
if (typeof window !== 'undefined') {
  // Capture unhandled React errors
  window.addEventListener('error', (event) => {
    if (event.message && (event.message.includes('Minified React error') || event.message.includes('React error #310'))) {
      console.error('[DEBUG REACT_ERROR] React error caught by global handler', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        stack: event.error?.stack,
        hypothesisId: 'REACT_ERROR_310'
      });
    }
  }, true);
  
  // Capture unhandled promise rejections (React errors sometimes appear here)
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && (String(event.reason).includes('React error') || String(event.reason).includes('#310'))) {
      console.error('[DEBUG REACT_ERROR] React error in promise rejection', {
        reason: event.reason,
        hypothesisId: 'REACT_ERROR_310'
      });
    }
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

// Ensure page starts at top on initial load
if (typeof window !== 'undefined') {
  window.scrollTo(0, 0);
  // Prevent scroll restoration on page load
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <App />
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
